import { useContext, useEffect, useMemo, useCallback } from "react";
// context
import { CartIPLSwiggySixesContext } from "@swiggy-private/core-ui";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isValidNumber, getClosingTimeEpochFromStorage } from "@swiggy-private/core-ui";
import { getClosingTimeFromApiInSeconds } from "@cart-client/helpers/TimerOnCart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { getIPLSwiggySixesDataFromCart } from "@cart-client/helpers/IPLSwiggySixes";
// types
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    SwiggySixesCroutonAPIInfo,
    SwiggySixesCroutonComms,
    TimerUpdateSource,
} from "@swiggy-private/cart-types";

export interface UseIPLSwiggySixesCartCroutonAndUpdatesProps {
    cartResponse: ReviewedCart | null;
    // When true, this instance does not push the cart timer into the shared timestamp store.
    // Used by the list-driven registration path in non-MRO so CartRenderer remains the sole owner.
    skipTimerUpdate?: boolean;
}

export const useIPLSwiggySixesCartCroutonAndUpdates = ({
    cartResponse,
    skipTimerUpdate = false,
}: UseIPLSwiggySixesCartCroutonAndUpdatesProps) => {
    const {
        validateAndSetTimestamp,
        croutonCommsState,
        tickerTimerInMinSec,
        timestampInMs,
        updateSource,
    } = useContext(CartIPLSwiggySixesContext);

    // True when the most recent accepted timer update came from the app JS bridge (a live six-hit).
    const isFromJSBridge = updateSource === TimerUpdateSource.APP_JS_BRIDGE;

    const { iplData, endTimestampInSec: timerValueFromCartData } = useMemo(
        () => getIPLSwiggySixesDataFromCart(cartResponse),
        [cartResponse],
    );

    // pull out restaurant timer data for comparison
    const fetchRestaurantClosingTimeInSec = useCallback(() => {
        return getClosingTimeFromApiInSeconds(cartResponse);
    }, [cartResponse]);

    // pull out restaurant closing time from storage
    const fetchRestaurantCloseEpoch = useCallback(() => {
        return getClosingTimeEpochFromStorage();
    }, []);

    // Cases -
    // if IPL timer < Restaurant Timer = Allow IPL Timer
    // if Restaurant Timer >= IPL Timer = Allow Restaurant Timer
    // if after sometime another six is hit and IPL Timer becomes more than Restaurant Timer - re-evaluate and give Restaurant Timer priority if less
    const allowIPLCroutonOverRestaurantTimer = useMemo(() => {
        // MRO uses crouton-list priority/tiebreak; restaurant-timer gating does not apply.
        if (isMROFlow()) {
            return true;
        }
        // IPL Ticker enabled and valid timestamp in storage
        if (
            tickerTimerInMinSec?.enabled &&
            !isEmpty(timestampInMs) &&
            isValidNumber(timestampInMs) &&
            timestampInMs > Date.now()
        ) {
            // NOTE - checking both closing duration and epoch in case local storage is not updated
            const restaurantCloseEpoch = fetchRestaurantCloseEpoch();
            const restaurantClosingTimeInSec = fetchRestaurantClosingTimeInSec();
            // CASE 1 - Restaurant close epoch is present and is valid in storage
            if (isValidNumber(restaurantCloseEpoch) && restaurantCloseEpoch > Date.now()) {
                return timestampInMs < restaurantCloseEpoch;
            }
            // CASE 2 - Restaurant closing time is present in response
            else if (isValidNumber(restaurantClosingTimeInSec) && restaurantClosingTimeInSec > 0) {
                const iplSecsLeft = Math.ceil((timestampInMs - Date.now()) / 1000);
                return iplSecsLeft < restaurantClosingTimeInSec;
            }
            // CASE 3 - Restaurant time is not present in storage or is invalid so enable IPL crouton
            return true;
        }
        // IPL Ticker not enabled or invalid timestamp in storage
        return false;
    }, [fetchRestaurantCloseEpoch, fetchRestaurantClosingTimeInSec, tickerTimerInMinSec?.enabled, timestampInMs]);

    const croutonData: SwiggySixesCroutonComms = useMemo(() => {
        const croutonComms: SwiggySixesCroutonAPIInfo["states"] | undefined = iplData?.states;
        const icon: string = iplData?.icon || "";

        if (isEmpty(croutonComms?.[croutonCommsState]) || isEmpty(icon)) {
            return { enabled: false };
        }

        // tickerTime enabled key is the control for the entire project which decide if comms need to be shown or not
        // until the ticker is enabled all comms will be shown on crouton + savings corner
        return {
            enabled: tickerTimerInMinSec?.enabled && allowIPLCroutonOverRestaurantTimer,
            croutonComms: croutonComms?.[croutonCommsState] || [],
            croutonState: croutonCommsState,
            icon,
        };
    }, [allowIPLCroutonOverRestaurantTimer, iplData, croutonCommsState, tickerTimerInMinSec?.enabled]);

    // update the timer in store as the timerValueChanges.
    // updateSource is only rewritten when validateAndSetTimestamp accepts a new timestamp (or cleared
    // on timer expiry in the provider) — never reset on every cartResponse, so a JS-bridge six-hit
    // keeps isFromJSBridge when a later cart update arrives without a newer IPL timestamp.
    useEffect(() => {
        if (skipTimerUpdate) {
            return;
        }
        if (!isEmpty(timerValueFromCartData) && isValidNumber(timerValueFromCartData)) {
            validateAndSetTimestamp?.(timerValueFromCartData, TimerUpdateSource.UPDATE_CART);
        }
    }, [timerValueFromCartData, validateAndSetTimestamp, skipTimerUpdate]);

    return {
        croutonData,
        allowIPLCroutonOverRestaurantTimer,
        // Whether the latest accepted timer update came from the app JS bridge. Drives the
        // static priority-1 registration of the IPL crouton (in-list or synthetic).
        isFromJSBridge,
    };
};
